import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { inventoryService } from "@/services/inventoryService";
import {
  CreateInventoryItemRequest,
  CreateInventoryMovementRequest,
  ConsumeStationSuppliesRequest,
  CreateStationSupplyRequirementRequest,
  ReplenishmentDashboard,
  UpdateInventoryItemRequest,
  UpdateStationSupplyRequirementRequest,
} from "@/types/inventory";

interface ApiError {
  response?: {
    data?: {
      message?: string;
    };
  };
}

export const useInventory = (slug: string) => {
  const queryClient = useQueryClient();

  const inventoryItemsQuery = useQuery({
    queryKey: ["inventory", "items", slug],
    queryFn: () => inventoryService.getInventoryItems(slug),
    enabled: !!slug,
    select: (response) => response.data?.data || [],
  });

  const stationConfigurationsQuery = useQuery({
    queryKey: ["inventory", "station-configurations", slug],
    queryFn: () => inventoryService.getStationConfigurations(slug),
    enabled: !!slug,
    select: (response) => response.data?.data || [],
  });

  const replenishmentDashboardQuery = useQuery({
    queryKey: ["inventory", "replenishment", slug],
    queryFn: async (): Promise<ReplenishmentDashboard | null> => {
      const response = await inventoryService.getReplenishmentDashboard(slug);
      return response.data?.data || null;
    },
    enabled: !!slug,
  });

  const inventoryMovementsQuery = useQuery({
    queryKey: ["inventory", "movements", slug],
    queryFn: () => inventoryService.getInventoryMovements(slug, { limit: 20 }),
    enabled: !!slug,
    select: (response) => response.data?.data || [],
  });

  const invalidateInventoryQueries = () => {
    queryClient.invalidateQueries({ queryKey: ["inventory", slug] });
    queryClient.invalidateQueries({ queryKey: ["inventory"] });
  };

  const createInventoryItem = useMutation({
    mutationFn: (data: CreateInventoryItemRequest) =>
      inventoryService.createInventoryItem(data),
    onSuccess: () => {
      invalidateInventoryQueries();
      toast.success("Material creado correctamente");
    },
    onError: (error: ApiError) => {
      toast.error(error.response?.data?.message || "Error al crear material");
    },
  });

  const updateInventoryItem = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateInventoryItemRequest }) =>
      inventoryService.updateInventoryItem(id, data),
    onSuccess: () => {
      invalidateInventoryQueries();
      toast.success("Material actualizado correctamente");
    },
    onError: (error: ApiError) => {
      toast.error(error.response?.data?.message || "Error al actualizar material");
    },
  });

  const deleteInventoryItem = useMutation({
    mutationFn: ({ id, company_id }: { id: string; company_id: string }) =>
      inventoryService.deleteInventoryItem(id, slug, company_id),
    onSuccess: () => {
      invalidateInventoryQueries();
      toast.success("Material desactivado");
    },
    onError: (error: ApiError) => {
      toast.error(error.response?.data?.message || "Error al desactivar material");
    },
  });

  const createStationConfiguration = useMutation({
    mutationFn: (data: CreateStationSupplyRequirementRequest) =>
      inventoryService.createStationConfiguration(data),
    onSuccess: () => {
      invalidateInventoryQueries();
      toast.success("Configuración de estación creada");
    },
    onError: (error: ApiError) => {
      toast.error(error.response?.data?.message || "Error al crear configuración");
    },
  });

  const updateStationConfiguration = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateStationSupplyRequirementRequest }) =>
      inventoryService.updateStationConfiguration(id, data),
    onSuccess: () => {
      invalidateInventoryQueries();
      toast.success("Configuración actualizada");
    },
    onError: (error: ApiError) => {
      toast.error(error.response?.data?.message || "Error al actualizar configuración");
    },
  });

  const deleteStationConfiguration = useMutation({
    mutationFn: ({ id, company_id }: { id: string; company_id: string }) =>
      inventoryService.deleteStationConfiguration(id, slug, company_id),
    onSuccess: () => {
      invalidateInventoryQueries();
      toast.success("Configuración desactivada");
    },
    onError: (error: ApiError) => {
      toast.error(error.response?.data?.message || "Error al desactivar configuración");
    },
  });

  const createInventoryMovement = useMutation({
    mutationFn: (data: CreateInventoryMovementRequest) =>
      inventoryService.createInventoryMovement(data),
    onSuccess: () => {
      invalidateInventoryQueries();
      toast.success("Movimiento registrado correctamente");
    },
    onError: (error: ApiError) => {
      toast.error(error.response?.data?.message || "Error al registrar movimiento");
    },
  });

  const consumeStationSupplies = useMutation({
    mutationFn: (data: ConsumeStationSuppliesRequest) =>
      inventoryService.consumeStationSupplies(data),
    onSuccess: () => {
      invalidateInventoryQueries();
      toast.success("Consumo aplicado correctamente");
    },
    onError: (error: ApiError) => {
      toast.error(error.response?.data?.message || "Error al aplicar consumo");
    },
  });

  return {
    inventoryItems: inventoryItemsQuery.data || [],
    isLoadingInventoryItems: inventoryItemsQuery.isLoading,
    stationConfigurations: stationConfigurationsQuery.data || [],
    isLoadingStationConfigurations: stationConfigurationsQuery.isLoading,
    replenishmentDashboard: replenishmentDashboardQuery.data,
    isLoadingReplenishmentDashboard: replenishmentDashboardQuery.isLoading,
    inventoryMovements: inventoryMovementsQuery.data || [],
    isLoadingInventoryMovements: inventoryMovementsQuery.isLoading,
    createInventoryItem,
    updateInventoryItem,
    deleteInventoryItem,
    createStationConfiguration,
    updateStationConfiguration,
    deleteStationConfiguration,
    createInventoryMovement,
    consumeStationSupplies,
  };
};
